/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { useState, Fragment, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { Card } from 'pnnl-react-core';
import { Grid, Row, Button } from 'react-bootstrap';
import { Menu, Dropdown, Alert } from 'antd';
import {
  FileImageOutlined,
  FilePdfOutlined,
  FileOutlined,
  FileExcelOutlined,
  DownOutlined,
  DownloadOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import { ToastContainer, toast } from 'react-toastify';

import { LoadingScreen } from 'components/loadingScreen';
import { Simple, Intermediate, Full, Numerical, Luox } from 'components/tabs';

import { excelExport, manageSession, convertLuoxData } from 'utils';

import * as htmlToImage from 'html-to-image';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import format from 'xml-formatter';
import fileDownload from 'js-file-download';
import { saveAs } from 'file-saver';
import XLSX from 'xlsx';

import { setLibraryData } from 'redux/actions';

import tm30ExampleLibrary from 'data/TM30-Example-Library.json';
import jsonPng from 'data/json-file.png';

const { SubMenu } = Menu;

const Report = () => {
  const dispatch = useDispatch();
  const reportData = useSelector((state) => state.dataReducers.reportData);
  // const reportData = tm30ExampleLibrary[0];
  const shouldDownloadPdf = useSelector(
    (state) => state.dataReducers.shouldDownloadPdf
  );
  const luoxData = useSelector((state) => state.dataReducers.luoxData);
  const luoxUnits = useSelector((state) => state.dataReducers.luoxUnits);
  const metadata = reportData.metadata;
  const reportName = metadata?.identifier;

  const [activeReportView, setActiveReportView] = useState('full');
  const [showSpinner, setShowSpinner] = useState(false);
  const [showMetadataActions, setShowMetadataActions] = useState(true);
  const [showAlertText, setShowAlertText] = useState({
    show: false,
    message: '',
  });
  const [formattedLuoxData, setFormattedLuoxData] = useState(null);

  useEffect(() => {
    const savedActiveReportView = JSON.parse(
      sessionStorage.getItem('session-choices')
    )?.activeReportView;

    if (savedActiveReportView && luoxData.luoxOutput) {
      setActiveReportView(savedActiveReportView);
    } else {
      setActiveReportView('full');
    }
  }, []);

  useEffect(() => {
    const duvTest = reportData.tm30?.Numerical_Results.DUV[0].toFixed(4);
    const cctTest = reportData.tm30?.Numerical_Results.CCT[0].toFixed(4);
    if (
      cctTest < 2136 ||
      cctTest > 7042 ||
      duvTest > 0.0091 ||
      duvTest < -0.018
    ) {
      const newAlert = {
        show: true,
        message:
          'The chromaticity is outside the range specified in ANSI C78.377-2017. Interpret results with caution.',
      };
      setShowAlertText(newAlert);
    }
  }, [reportData]);

  const handleJSONDownload = () => {
    if (Object.keys(reportData).length !== 0) {
      const json = JSON.stringify(reportData);
      fileDownload(json, `${reportName !== '' ? reportName : 'sample'}.json`);
    }
  };

  const handleXMLDownload = () => {
    let js2xmlparser = require('js2xmlparser');

    const spdData = reportData.tm30.input.Wavelength?.map((data, index) => {
      const spdObj = {
        '@': {
          wavelength: data,
        },
        '#': reportData.tm30.input.SPD[index],
      };
      return spdObj;
    });

    const getHeaderInfo = (header) => {
      if (metadata[header]) {
        return metadata[header];
      } else {
        return 'N/A';
      }
    };

    const jsonToXmlObject = {
      '@': {
        xmlns: 'iestm2714',
        version: '1.0',
      },
      Header: {
        Manufacturer: getHeaderInfo('manufacturer'),
        CatalogNumber: getHeaderInfo('model'),
        Description: getHeaderInfo('description'),
        FileCreator: getHeaderInfo('creator'),
        Laboratory: getHeaderInfo('laboratory'),
        UniqueIdentifier: getHeaderInfo('identifier'),
        ReportNumber: getHeaderInfo('report_number'),
        ReportDate: getHeaderInfo('report_date'),
        DocumentCreationDate: getHeaderInfo('creation_date'),
        Comments: getHeaderInfo('comments'),
      },
      SpectralDistribution: {
        SpectralQuantity: 'relative',
        BandwidthFWHM: '2.0',
        BandwidthCorrected: 'true',
        SpectralData: spdData,
      },
    };

    const json2xml = js2xmlparser.parse('IESTM2714', jsonToXmlObject);
    const formattedXml = format(json2xml, {
      indentation: '\t',
      collapseContent: true,
    });
    fileDownload(formattedXml, `${reportName ? reportName : 'sample'}.spdx`);
  };

  const handleLuoxDataDownload = () => {
    formattedLuoxData.forEach((data) => delete data.key);
    const fileType =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';
    const ws = XLSX.utils.json_to_sheet(formattedLuoxData);
    const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: fileType });
    saveAs(data, reportName !== '' ? reportName : 'sample' + fileExtension);
  };

  const handlePdfDownload = (pdfScale) => {
    setShowSpinner(true);
    setShowMetadataActions(false);
    let margins = [];
    let mode = 'p';
    let pdfDownloadTimeout = 0;
    switch (activeReportView) {
      case 'simple':
        margins = [40, 20, 130, 150]; // left, top, right, bottom
        break;
      case 'intermediate':
        margins = [20, 30, 260, 150];
        mode = 'l';
        pdfDownloadTimeout = 500;
        break;
      case 'full':
        margins = [5, 10, 200, 250];
        pdfDownloadTimeout = 500;
        break;
      default:
        break;
    }

    // Detect Safari
    let safariAgent = navigator.userAgent?.indexOf('Safari') > -1;
    let chromeAgent = navigator.userAgent?.indexOf('Chrome') > -1;

    // Discard Safari since it also matches Chrome
    if (chromeAgent && safariAgent) safariAgent = false;

    setTimeout(() => {
      if (safariAgent) {
        htmlToImage
          .toPng(document.getElementById(activeReportView))
          .then((dataUrl) => {
            let pdf = new jsPDF(mode, 'mm', 'a4', true);
            pdf.addImage(dataUrl, 'PNG', ...margins, '', 'FAST');
            pdf.save(
              reportName
                ? `${reportName}-${activeReportView}.pdf`
                : `sample-${activeReportView}.pdf`
            );
            setShowSpinner(false);
            setShowMetadataActions(true);
          });
      } else {
        html2canvas(document.getElementById(activeReportView), {
          scale: pdfScale,
        }).then((canvas) => {
          let pdf = new jsPDF(mode, 'mm', 'a4', true);
          pdf.addImage(
            canvas.toDataURL('image/png'),
            'PNG',
            ...margins,
            '',
            'FAST'
          );
          pdf.save(
            reportName
              ? `${reportName}-${activeReportView}.pdf`
              : `sample-${activeReportView}.pdf`
          );
          setShowSpinner(false);
          setShowMetadataActions(true);
        });
      }
    }, [pdfDownloadTimeout]);
  };

  const handleHTMLtoImage = () => {
    if (
      activeReportView === 'intermediate' ||
      activeReportView === 'simple' ||
      activeReportView === 'full'
    ) {
      setShowSpinner(true);
      setShowMetadataActions(false);

      let fileName = reportName
        ? `${reportName}-${activeReportView}.png`
        : `sample-${activeReportView}.png`;
      const imageDownloadTimeout = activeReportView === 'simple' ? 0 : 500;

      // Detect Safari
      let safariAgent = navigator.userAgent?.indexOf('Safari') > -1;
      let chromeAgent = navigator.userAgent?.indexOf('Chrome') > -1;

      // Discard Safari since it also matches Chrome
      if (chromeAgent && safariAgent) safariAgent = false;

      setTimeout(() => {
        // if (safariAgent) {
        // htmlToImage
        //   .toPng(document.getElementById(activeReportView))
        //   .then((dataUrl) => {
        //     saveAs(dataUrl, fileName);
        //     setShowSpinner(false);
        //     setShowMetadataActions(true);
        //   });
        // } else {
        html2canvas(document.getElementById(activeReportView), {
          scale: 1,
        }).then((canvas) => {
          const image = canvas.toDataURL('image/png');
          saveAs(image, fileName);
          setShowSpinner(false);
          setShowMetadataActions(true);
        });
        // }
      }, imageDownloadTimeout);
    }
  };

  const addToLibrary = () => {
    toast.success(
      <div>
        <h3>Success</h3>{' '}
        <p>
          Successfully added to the User Library.{' '}
          <Link to='/user-library'>Click here</Link> for library.
        </p>
      </div>
    );

    // Standardizing units for user library
    if (luoxData.luoxOutput) {
      const libraryStandardLuoxUnits = {
        ...luoxUnits,
        powerScale: 'mW',
        areaScale: 'm2',
      };
      const covertedLuoxData = convertLuoxData(
        luoxData.luoxInput,
        libraryStandardLuoxUnits
      );
      const newReportData = Object.assign({}, reportData, {
        luoxData: { ...covertedLuoxData, luoxUnits: libraryStandardLuoxUnits },
      });
      dispatch(setLibraryData([newReportData]));
    } else {
      dispatch(setLibraryData([reportData]));
    }
  };

  const handleViewChange = (e) => {
    setActiveReportView(e.key);
    manageSession({ activeReportView: e.key });
  };

  let displayView;
  let numericalTable;
  if (Object.keys(reportData).length !== 0) {
    const { Numerical_Results, input } = reportData && reportData.tm30;

    switch (activeReportView) {
      case 'numerical':
        displayView = <Numerical reportData={reportData} />;
        break;
      case 'simple':
        displayView = <Simple tm30={Numerical_Results} />;
        break;
      case 'intermediate':
        displayView = (
          <Intermediate
            tm30={Numerical_Results}
            downloading={showSpinner || shouldDownloadPdf}
          />
        );
        break;
      case 'full':
        displayView = (
          <Full
            active={activeReportView}
            reportData={reportData}
            downloading={showSpinner || shouldDownloadPdf}
            showMetadataActions={showMetadataActions}
          />
        );
        break;
      case 'luox':
        displayView = luoxData.luoxOutput && (
          <Luox
            luoxData={luoxData}
            setFormattedLuoxData={setFormattedLuoxData}
            measurementName={
              reportData.metadata.identifier
                ? reportData.metadata.identifier
                : 'Measurement'
            }
            showEditOptions={true}
          />
        );
      default:
        break;
    }
  }

  const downloadMenu = (
    <Menu>
      <SubMenu
        disabled={['numerical', 'luox'].includes(activeReportView)}
        icon={<FileImageOutlined style={{ fontSize: '20px' }} />}
        title='Current Report Image'
      >
        <Menu.Item
          onClick={handleHTMLtoImage}
          icon={<FileImageOutlined style={{ fontSize: '20px' }} />}
        >
          Image
        </Menu.Item>
        <SubMenu
          icon={<FilePdfOutlined style={{ fontSize: '20px' }} />}
          title='PDF'
        >
          <Menu.Item onClick={() => handlePdfDownload(1)}>
            Low resolution
          </Menu.Item>
          <Menu.Item onClick={() => handlePdfDownload(2)}>
            High resolution
          </Menu.Item>
        </SubMenu>
      </SubMenu>
      <Menu.Item
        onClick={handleXMLDownload}
        icon={<FileOutlined style={{ fontSize: '20px' }} />}
      >
        SPDX
      </Menu.Item>
      <SubMenu
        title='Complete TM-30 Data'
        icon={<DatabaseOutlined style={{ fontSize: '20px' }} />}
      >
        <Menu.Item onClick={handleJSONDownload}>
          <img alt='JSON-icon' src={jsonPng} css={JsonDownloadIconStyle} /> JSON
        </Menu.Item>
        <SubMenu
          icon={<FileExcelOutlined style={{ fontSize: '20px' }} />}
          title='XLSX'
        >
          <Menu.Item
            onClick={() => excelExport(reportData, reportName, 'full')}
          >
            Full Precision
          </Menu.Item>
          <Menu.Item
            onClick={() => excelExport(reportData, reportName, 'rounded')}
          >
            Rounded Precision
          </Menu.Item>
        </SubMenu>
      </SubMenu>
      {luoxData.luoxOutput && ['luox'].includes(activeReportView) && (
        <Menu.Item
          onClick={handleLuoxDataDownload}
          icon={<FileExcelOutlined style={{ fontSize: '20px' }} />}
        >
          Download Alpha-Opic Data - XLSX
        </Menu.Item>
      )}
    </Menu>
  );

  const reportTypeMenu = (
    <Menu onClick={handleViewChange}>
      <SubMenu title='IES TM-30'>
        <Menu.Item key='simple'>Simple</Menu.Item>
        <Menu.Item key='intermediate'>Intermediate</Menu.Item>
        <Menu.Item key='full'>Full</Menu.Item>
        <Menu.Item key='numerical'>Numerical</Menu.Item>
      </SubMenu>
      <SubMenu title='CIE S026'>
        <Menu.Item key='luox' disabled={!luoxData.luoxOutput}>
          Alpha-opic Data
        </Menu.Item>
      </SubMenu>
    </Menu>
  );

  const reportHeader = (
    <div css={ReportHeaderStyle}>
      {reportName === '' ? (
        <h4 css={ReportTitleStyle}>No SPD Name Specified</h4>
      ) : (
        <h4 css={ReportTitleStyle}>{reportName}</h4>
      )}
      <div css={ActionsMenuContainerStyle}>
        <Dropdown trigger='click' overlay={reportTypeMenu}>
          <Button bsStyle='primary'>
            {activeReportView !== 'luox'
              ? `TM-30 ${
                  activeReportView.charAt(0).toUpperCase() +
                  activeReportView.slice(1)
                } Report`
              : 'Alpha-opic Data'}{' '}
            <DownOutlined />
          </Button>
        </Dropdown>
        <Dropdown
          css={ActionButtonStyle}
          trigger='click'
          overlay={downloadMenu}
        >
          <Button bsStyle='info'>
            Download <DownOutlined />
          </Button>
        </Dropdown>
        <Button bsStyle='success' onClick={addToLibrary}>
          Add to User Library
        </Button>
      </div>
    </div>
  );

  const emptyReportPage = (
    <div css={EmptyReportPageStyle}>
      <h3>No data calculated</h3>
      <p>
        Use the <Link to='/calculator'>Data Input</Link>,{' '}
        <Link to='/user-library'>User Library</Link> or{' '}
        <Link to='/example-library'>Example Library</Link> pages to initiate a
        calculation and generate reports.
      </p>
    </div>
  );

  return (
    <Fragment>
      {/* {activeReportView === 'numerical' ? (
          <Card style={{ margin: '2rem' }}>
            {Object.keys(reportData).length !== 0 ? (
              <div>
                {reportHeader}
                <div css={DividerStyle}></div>
                {numericalTable}
              </div>
            ) : (
              emptyReportPage
            )}
          </Card>
        ) : ( */}
      <Grid style={{ margin: '5rem auto' }}>
        <Card>
          <div css={DownloadSpinner}>
            <LoadingScreen show={showSpinner || shouldDownloadPdf} />
          </div>
          {showAlertText.show && (
            <Alert message={showAlertText.message} type='warning' closable />
          )}
          <div css={ReportContainer(showSpinner || shouldDownloadPdf)}>
            {Object.keys(reportData).length !== 0 ? (
              <div>
                {reportHeader}
                <div css={DividerStyle}></div>
                {displayView}
              </div>
            ) : (
              emptyReportPage
            )}
          </div>
        </Card>
      </Grid>
      {/* )} */}
    </Fragment>
  );
};

export default Report;

const ReportContainer = (loading) => css`
  opacity: ${loading ? 0.4 : 1};

  @media (max-width: 992px) {
    width: ${loading ? 'max-content' : 'auto'};
  }

  svg {
    pointer-events: ${loading ? 'fill !important' : 'auto'};
  }
`;

const EmptyReportPageStyle = css`
  margin: 1rem;
  text-align: center;
`;

const ActionButtonStyle = css`
  margin: 0rem 1rem;
`;

const ReportHeaderStyle = css`
  display: flex;
  justify-content: space-between;

  h2 {
    margin: 1.5rem 0;
    color: #d77600;
  }
`;

const ActionsMenuContainerStyle = css`
  display: flex;
  justify-content: center;
  margin-bottom: 5px;
  height: fit-content;

  p {
    margin: 10px;
    font-weight: bold;
  }

  margin: 10px 0px 10px 10px;
`;

const JsonDownloadIconStyle = css`
  margin-left: -3px;
  margin-right: 3px;
`;

const DownloadIconsStyle = css`
  font-size: 20px;
`;

const DownloadSpinner = css`
  position: absolute;
  top: 4%;
  left: 48%;
  z-index: 10;
`;

const DividerStyle = css`
  border-top: 1px solid grey;
  margin: 1rem 0 2rem 0;
`;

const ReportTitleStyle = css`
  margin: 30px 0 0 0;
`;
